import * as React from "react";
import {
  Avatar,
  IconCheck,
  IconChevronDown,
  IconExternalLink,
  IconLogOut,
  IconMonitor,
  IconMoon,
  IconSun,
  initialsFromUser,
} from "../primitives/Avatar";
import { useThemeContext } from "../../theme/ThemeProvider";
import { FONT_SIZES, MODES, THEMES } from "../../theme/theme";
import type { FontSizeId, ThemeId, ThemeMode } from "../../theme/theme";

export type ProfileMenuProps = {
  userName?: string;
  userEmail?: string;
  logoutPending?: boolean;
  onLogout?: () => void;
  onGoToPlatform?: () => void;
};

const modeIcons: Record<ThemeMode, React.FC<{ size?: number }>> = {
  light: IconSun,
  dark: IconMoon,
  system: IconMonitor,
};

export const ProfileMenu: React.FC<ProfileMenuProps> = ({
  userName,
  userEmail,
  logoutPending,
  onLogout,
  onGoToPlatform,
}) => {
  const [open, setOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const { mode, setMode, theme, setTheme, fontSize, setFontSize } = useThemeContext();
  const initials = initialsFromUser(userName, userEmail);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="plugin-profile" ref={menuRef}>
      <button
        type="button"
        className="plugin-profile__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label="Account menu"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Avatar who={initials} size={22} fontSize={9} name={userName || userEmail} />
        <IconChevronDown size={12} style={{ color: "var(--muted)" }} />
      </button>

      {open && (
        <div className="plugin-profile__menu" role="menu">
          <div className="plugin-profile__user">
            <Avatar who={initials} size={32} fontSize={12} name={userName || userEmail} />
            <div className="plugin-profile__user-text">
              <div className="plugin-profile__user-name">{userName || "Signed in"}</div>
              {userEmail ? <div className="plugin-profile__user-email">{userEmail}</div> : null}
            </div>
          </div>

          <div className="plugin-profile__divider" />

          {onGoToPlatform ? (
            <button
              type="button"
              role="menuitem"
              className="plugin-profile__item"
              onClick={() => {
                setOpen(false);
                onGoToPlatform();
              }}
            >
              <IconExternalLink size={14} style={{ color: "var(--muted)" }} />
              Go to platform
            </button>
          ) : null}

          <div className="plugin-profile__divider" />

          <div className="plugin-profile__section">
            <div className="plugin-profile__section-label">Theme</div>
            <div className="plugin-profile__mode-grid">
              {MODES.map((m) => {
                const Icon = modeIcons[m.id];
                const active = mode === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    title={m.label}
                    className={`plugin-profile__mode-btn${active ? " is-active" : ""}`}
                    onClick={() => setMode(m.id)}
                  >
                    <Icon size={12} />
                    <span>{m.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="plugin-profile__divider" />

          <div className="plugin-profile__section">
            <div className="plugin-profile__section-label">Accent</div>
            <div className="plugin-profile__swatches">
              {THEMES.map((t) => {
                const active = theme === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    title={t.label}
                    aria-label={t.label}
                    className={`plugin-profile__swatch${active ? " is-active" : ""}`}
                    style={{ background: t.accent }}
                    onClick={() => setTheme(t.id as ThemeId)}
                  >
                    {active ? <IconCheck size={12} style={{ color: "#fff" }} /> : null}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="plugin-profile__divider" />

          <div className="plugin-profile__section">
            <div className="plugin-profile__section-label">Font size</div>
            <div className="plugin-profile__fs-grid">
              {FONT_SIZES.map((f) => {
                const active = fontSize === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    className={`plugin-profile__fs-btn${active ? " is-active" : ""}`}
                    onClick={() => setFontSize(f.id as FontSizeId)}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>

          {onLogout ? (
            <>
              <div className="plugin-profile__divider" />
              <button
                type="button"
                role="menuitem"
                className="plugin-profile__item plugin-profile__item--danger"
                disabled={logoutPending}
                onClick={() => {
                  setOpen(false);
                  onLogout();
                }}
              >
                <IconLogOut size={14} />
                {logoutPending ? "Signing out…" : "Log out"}
              </button>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};
